<template>
  <form
    class="search-bar"
    @submit.prevent="handleFormSubmit"
  >
    <label
      v-if="isMobile"
      class="text-utility-label-s search-bar__label"
      v-text="$string('search.search')"
    />
    <input
      v-model="input"
      class="search-bar__input text-body-s"
      :placeholder="$string('general.site_header.search.placeholder')"
      type="search"
      @focus="handleFocus"
    >

    <button
      class="search-bar__submit"
      type="submit"
      @blur="handleButtonBlur"
    >
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.search.submit')"
      />

      <icon-search />
    </button>

    <button
      v-if="searchQuery !== ''"
      class="search-bar__clear text-body-s-link"
      type="submit"
      @click="$emit('clear')"
      v-text="$string('search.clear')"
    />
  </form>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Used in the header.
 * - Acts as a small placeholder to only load predictive search when required.
 *
 * @param {Object} redirect - Boost redirect URL.
 * @param {String} searchQuery - Current search query, used to update input
 * value when clicking suggestion.
 *
 */
import { mapState } from 'vuex'

import IconSearch from '~icons/misc/search.svg'

export default {
  name: 'SearchBar',

  components: {
    IconSearch,
  },

  props: {
    redirect: {
      type: String,
      default: '',
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-focus': (payload) => typeof payload === 'boolean',
    'search-input': (payload) => typeof payload === 'string',
    clear: () => true,
  },

  data() {
    return {
      input: '',
      query: '',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      tabbable: (state) => state.accessibility.tabbable,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute form submit URL.
     * - Use special URL in redirect to make redirect URL relative.
     * @returns {String}
     */
    submitUrl() {
      const formattedRedirect = this.redirect.replace(
        'https://stephenwebsterredirect.com/',
        this.$variable('routes.normalisedRoot'),
      )

      return this.redirect
        ? formattedRedirect
        : `${this.$variable('routes.search')}?q=${this.input}`
    },
  },

  watch: {

    /**
     * Watch input to debounce update query.
     * - Debounces input to set query only when finished typing.
     * - Don't watch query to emit event because search query can update query
     *   when user clicks on a suggestion, don't want to trigger two updates.
     */
    input(query) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        this.query = query
        this.$emit('search-input', this.query)
      }, 300)
    },

    /**
     * Watch search query to update query.
     */
    searchQuery() {
      this.input = this.searchQuery
    },
  },

  methods: {

    /**
     * Handle focus to emit event.
     */
    handleFocus() {
      this.$emit('search-focus', true)
    },

    /**
     * Handle button blur.
     * - Assumes that if tabbable is active, there is no query, and button blurs
     *   then the user wants to tab outside the search bar so predictive search
     *   should close.
     */
    handleButtonBlur() {
      if (!this.tabbable || this.query) {
        return
      }

      this.$emit('search-focus', false)
    },

    /**
     * Handle form submit.
     */
    handleFormSubmit() {
      location.href = this.submitUrl
    },
  },
}
</script>

<style lang="scss">
@import './search-bar';
</style>
